import { BrowserProvider, toQuantity } from "ethers";
import {
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  createContext,
  useCallback,
  useEffect,
} from "react";

import { AsyncTask, ConnectedProvider, WalletEvent } from "../domain";

interface Web3ProviderContext {
  connectProvider: () => Promise<void>;
  connectedProvider: AsyncTask<ConnectedProvider, string>;
  disconnectProvider: () => void;
}

const web3ProviderContextNotReadyErrorMsg = "Provider context is not ready yet";

const web3ProviderContext = createContext<Web3ProviderContext>({
  connectedProvider: { status: "pending" },
  connectProvider: () => Promise.reject(web3ProviderContextNotReadyErrorMsg),
  disconnectProvider: () => Promise.reject(web3ProviderContextNotReadyErrorMsg),
});

export const Web3ProviderProvider: FC<PropsWithChildren> = (props) => {
  const [connectedProvider, setConnectedProvider] = useState<
    AsyncTask<ConnectedProvider, string>
  >({
    status: "pending",
  });
  // This is a hack to workaround this MetaMask issue:
  // https://github.com/MetaMask/metamask-extension/issues/13375
  const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);
  const isSwitchingNetworkDelay = 1500;

  const connectProvider = useCallback(async () => {
    if (window.ethereum) {
      setConnectedProvider({ status: "loading" });

      const provider = new BrowserProvider(window.ethereum);

      try {
        await provider.send("eth_requestAccounts", []);

        const signer = await provider.getSigner();
        const address = await signer.getAddress();
        const chainId = (await provider.getNetwork()).chainId;

        if (chainId !== BigInt(import.meta.env.VITE_APP_CHAIN_ID)) {
          setIsSwitchingNetwork(true);
          await provider.send("wallet_switchEthereumChain", [
            { chainId: toQuantity(import.meta.env.VITE_APP_CHAIN_ID) },
          ]);
          setTimeout(() => {
            setIsSwitchingNetwork(false);
          }, isSwitchingNetworkDelay);
        }

        setConnectedProvider({
          status: "successful",
          data: {
            address: address.toLowerCase(),
            provider: provider,
            chainId,
          },
        });
      } catch (e) {
        console.error(e);
        setConnectedProvider({
          status: "failed",
          error: "An error occurred while connecting your Wallet",
        });
      }
    } else {
      setConnectedProvider({
        status: "failed",
        error: "MetaMask is not installed",
      });
    }
  }, [setConnectedProvider, setIsSwitchingNetwork]);

  const disconnectProvider = useCallback(() => {
    if (!isSwitchingNetwork) {
      setConnectedProvider({ status: "pending" });
    }
  }, [setConnectedProvider]);

  useEffect(() => {
    if (window.ethereum) {
      const provider = new BrowserProvider(window.ethereum);

      setConnectedProvider({ status: "loading" });

      Promise.all([
        window.ethereum.request({ method: "eth_accounts" }),
        provider.getNetwork(),
      ])
        .then(([accounts, network]) => {
          if (network.chainId !== BigInt(import.meta.env.VITE_APP_CHAIN_ID)) {
            setConnectedProvider({ status: "pending" });
          } else {
            const connectedAccount = accounts[0];

            if (connectedAccount && typeof connectedAccount === "string") {
              setConnectedProvider({
                status: "successful",
                data: {
                  address: connectedAccount.toLowerCase(),
                  provider,
                  chainId: network.chainId,
                },
              });
            } else {
              setConnectedProvider({ status: "pending" });
            }
          }
        })
        .catch(() => {
          setConnectedProvider({ status: "pending" });
        });
    }
  }, [setConnectedProvider]);

  useEffect(() => {
    const onAccountsChanged = (addresses: string[]) => {
      if (addresses[0] && connectedProvider.status === "successful") {
        setConnectedProvider({
          status: "successful",
          data: {
            ...connectedProvider.data,
            address: addresses[0],
          },
        });
      } else {
        setConnectedProvider({
          status: "pending",
        });
      }
    };

    const onChainChanged = (newChainId: string) => {
      if (newChainId !== toQuantity(import.meta.env.VITE_APP_CHAIN_ID)) {
        disconnectProvider();
      }
    };

    const onDisconnect = () => {
      disconnectProvider();
    };

    if (
      window.ethereum &&
      "on" in window.ethereum &&
      typeof window.ethereum.on === "function"
    ) {
      window.ethereum.on(WalletEvent.ACCOUNTS_CHANGED, onAccountsChanged);
      window.ethereum.on(WalletEvent.CHAIN_CHANGED, onChainChanged);
      window.ethereum.on(WalletEvent.DISCONNECT, onDisconnect);
    }

    return () => {
      if (
        window.ethereum &&
        "removeListener" in window.ethereum &&
        typeof window.ethereum.removeListener === "function"
      ) {
        window.ethereum.removeListener(
          WalletEvent.ACCOUNTS_CHANGED,
          onAccountsChanged
        );
        window.ethereum.removeListener(
          WalletEvent.CHAIN_CHANGED,
          onChainChanged
        );
        window.ethereum.removeListener(WalletEvent.DISCONNECT, onDisconnect);
      }
    };
  }, [connectedProvider, setConnectedProvider, disconnectProvider]);

  const value = useMemo(
    () => ({
      connectedProvider,
      connectProvider,
      disconnectProvider,
    }),
    [connectedProvider, connectProvider, disconnectProvider]
  );

  return <web3ProviderContext.Provider value={value} {...props} />;
};

export const useWeb3ProviderContext = (): Web3ProviderContext => {
  return useContext(web3ProviderContext);
};
